"use client";

import React, { useState } from "react";
import {
  TabSection,
  TabCard,
} from "@/.storyblok/types/319751/storyblok-components";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import { renderRichText } from "@/lib/richText";
import Link from "next/link";
import Image from "next/image";

interface TabSectionProps {
  blok?: TabSection & SbBlokData;
}

const TabSectionComponent: React.FC<TabSectionProps> = ({ blok }) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  if (!blok) return null;
  const { title, subtitle, tabs, tab_type, left_icon, right_icon, left_title, right_title, bg_style } = blok;

  const backgroundClass = bg_style === "dark" ? "bg-light" : "";


  if (!tabs || tabs.length === 0) return null;

  const isVertical = tab_type === "tab_vertical";

  if (isVertical) {
    return (
      <div {...storyblokEditable(blok)} className={`${backgroundClass} crest-vs-owasp mb-lg-5 component-tabs py-4 py-md-5`}>
        <div className="container lead">
          {title && <h2 className="text-secondary text-center mb-4">{title}</h2>}
          {subtitle && (
            <div className="lead mb-5 text-center max-two-thirds" dangerouslySetInnerHTML={{ __html: renderRichText(subtitle) }} />
          )}
          <div className="row g-0">
            <div className="col-24 col-lg-6">
              <ul className={`nav nav-tabs tabs-vertical d-flex flex-column nav-tab-count-${tabs.length}`} id="content-tabs" role="tablist">
                {tabs.map((tab: TabCard, index) => (
                  <li key={tab._uid} className="nav-item" role="presentation">
                    <button
                      className={`nav-link p-3 fs-4 ${index === activeTabIndex ? 'active' : ''}`}
                      id={`${tab._uid}-tab`}
                      data-bs-toggle="tab"
                      data-bs-target={`#${tab._uid}-tab-pane`}
                      type="button"
                      role="tab"
                      aria-controls={`${tab._uid}-tab-pane`}
                      aria-selected={index === activeTabIndex}
                      onClick={() => setActiveTabIndex(index)}
                    >
                      {tab.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-24 col-lg-18 tab-content">
              {tabs.map((tab: TabCard, index) => (
                <div
                  key={`${tab._uid}-content`}
                  className={`tab-pane fade card card-lighter mb-4 mb-md-0 lead p-4 p-xl-5 ${
                    index === activeTabIndex ? 'active show' : ''
                  }`}
                  id={`${tab._uid}-tab-pane`}
                  role="tabpanel"
                  aria-labelledby={`${tab._uid}-tab`}
                  tabIndex={0}
                >
                  <h3 className="h2 mb-4 text-secondary">{tab.title}</h3>
                  <div className="row justify-content-between g-4 g-xl-5">
                    <div className="col-24 col-md">
                      <div className="row row-divide gx-5">
                        <div className="col-24 col-lg-12">
                          {left_icon?.filename && (
                            <Image
                              width={150}
                              height={150}
                              className="mx-auto"
                              src={left_icon.filename}
                              alt={left_icon.alt || ""}
                              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                              loading="lazy"
                              quality={75}
                            />
                          )}
                          <h4>{left_title}</h4>
                          {tab.left_body && (
                            <div dangerouslySetInnerHTML={{ __html: renderRichText(tab.left_body) }} />
                          )}
                        </div>
                        <div className="col-24 col-lg-12">
                          {right_icon?.filename && (
                            <Image
                              width={150}
                              height={150}
                              className="mx-auto py-4"
                              src={right_icon.filename} alt={right_icon.alt || ""}
                              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                              loading="lazy"
                              quality={75}
                            />
                          )}
                          <h4>{right_title}</h4>
                          {tab.right_body && (
                            <div dangerouslySetInnerHTML={{ __html: renderRichText(tab.right_body) }} />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      {...storyblokEditable(blok)}
      className={`container pt-4 mt-2 pt-md-5 mt-md-3 pb-md-5 mb-md-5 ${backgroundClass}`}
    >
      {title && (
        <h2
          className={`text-center mb-4 pb-2 ${blok.title_color ? "text-" + blok.title_color : "text-primary"}`}
        >
          {title}
        </h2>
      )}
      {subtitle && (
        <div
          className="lead text-center mb-4 pb-2 mb-md-5 pb-md-3"
          dangerouslySetInnerHTML={{ __html: renderRichText(subtitle) }}
        />
      )}

      <div className="accordion-tabs tabs-with-icon mb-0">
        {tabs.map((tab: TabCard, index) => (
          <input
            key={`tab-input-${tab._uid}`}
            className="state"
            type="radio"
            title={`tab-${tab._uid}`}
            name={`tabs-state-${blok._uid}`}
            id={`ptypetab-${tab._uid}`}
            checked={index === activeTabIndex}
            onChange={() => setActiveTabIndex(index)}
            aria-label={`tab-${tab._uid}`}
          />
        ))}

        <div className="tabs flex-tabs">
          {tabs.map((tab: TabCard, index) => (
            <label
              key={`label-${tab._uid}`}
              htmlFor={`ptypetab-${tab._uid}`}
              id={`ptypetab-${tab._uid}-label`}
              className={`tab py-3${index === activeTabIndex ? " is-active" : ""}`}
              onClick={() => setActiveTabIndex(index)}
            >
              {tab.topic && (
                <div className="package-type">
                  <span
                    className={`badge font-weight-light ${
                      tab.topic === "Infrastructure"
                        ? ""
                        : tab.topic === "Application"
                          ? "badge-red"
                          : tab.topic === "Cloud"
                            ? "badge-blue"
                            : tab.topic === "Targeted"
                              ? "badge-green"
                              : ""
                    }`}
                  >
                    {tab.topic}
                  </span>
                </div>
              )}
              {tab.icon && tab.icon.filename && (
                <div className="tab-icon mb-3">
                  <Image
                    src={tab.icon.filename}
                    height={80}
                    width={80}
                    alt=""
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    loading="lazy"
                    quality={75}
                  />
                </div>
              )}
              <div className="tab-title">{tab.title}</div>
              {tab.price && (
                <div className="package-price">From {tab.price}</div>
              )}
            </label>
          ))}

          {tabs.map((tab: TabCard, index) => (
            <div
              key={`panel-${tab._uid}`}
              id={`ptypetab-${tab._uid}-panel`}
              className={`panel${index === activeTabIndex ? " active show" : ""}`}
            >
              <div className="panel-content">
                <div className="panel-text">
                  <h3>
                    {tab.topic && <>{tab.topic} - </>}
                    {tab.title}
                  </h3>
                  {tab.description && (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: renderRichText(tab.description),
                      }}
                    />
                  )}
                  {tab.cta_button && tab.cta_button_link?.url && (
                    <Link
                      href={tab.cta_button_link.url || "#getQuote"}
                      className="button button-large button-primary js-cta-button mt-4"
                    >
                      {tab.cta_button}
                    </Link>
                  )}
                </div>
                {tab.description_image?.filename && (
                  <div className="panel-image">
                    <Image
                      src={tab.description_image.filename}
                      alt={tab.description_image.alt || ""}
                      width={140}
                      height={140}
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      loading="lazy"
                      quality={75}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TabSectionComponent;
